import {
  Box,
  Button,
  Hide,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';
import { format } from '@cloudinary/url-gen/actions/delivery';
import { webp } from '@cloudinary/url-gen/qualifiers/format';
import {
  CarouselContent,
  CarouselOverlay,
  CarouselProvider,
  Divider,
  Grid,
  PlayButton,
  VideoLightbox,
  WrappedImage,
} from '@time-line/components/common';
import { ratioToDecimal } from '@time-line/components/utils';
import { cloudinary } from 'lib/cloudinary';
import Image from 'next/image';
import { useMemo, useState } from 'react';
import type { SocialMediaStory } from 'types/cms/SocialMediaStory';

import { ProductSeeItInActionTitle } from './ProductSeeItInActionTitle';

const NUM_CARDS_VISIBLE = 5;
const ASPECT = '4:5';
const [ASPECT_W, ASPECT_H] = ASPECT.split(':').map(Number);
const SPACE_BETWEEN = 8;
const POSTER_SIZES = '(min-width: 992px) 12vw, 28vw';

export interface ProductSeeItInActionProps {
  title: string;
  items: SocialMediaStory[];
}

export const ProductSeeItInAction = ({
  title,
  items,
}: ProductSeeItInActionProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const itemsWithPosters = useMemo(
    () =>
      items.map((item) => {
        const { poster, video } = item;
        if (poster) return item;
        const posterUrl = cloudinary
          .video(`${video.cloudinaryId}.webp`)
          .addTransformation(`so_0`)
          .delivery(format(webp()))
          .toURL();

        return { ...item, poster: posterUrl };
      }),
    [items],
  );

  const [activeMedia, setActiveMedia] = useState(itemsWithPosters[0]);

  const slidesPerView = useBreakpointValue(
    { base: 3.5, md: 4.5, lg: NUM_CARDS_VISIBLE },
    { fallback: 'lg' },
  );

  return (
    <CarouselProvider>
      <Box>
        <Divider />
        <ProductSeeItInActionTitle title={title} />
        <Grid
          position="relative"
          overflow="hidden"
          borderRadius="base"
          width={{ base: '100vw', lg: '100%' }}
          ml={{ base: 'calc(50% - 50vw)', lg: 0 }}
          sx={{
            '.swiper-horizontal': {
              overflow: 'visible',
              display: 'grid',
              gridTemplateColumns: 'subgrid',
              gridColumn: { base: 'main-start / full-end', lg: 'full' },
              width: '100%',
            },
          }}
        >
          <Hide below="lg">
            <CarouselOverlay />
          </Hide>
          <CarouselContent
            slidesPerView={slidesPerView}
            spaceBetween={SPACE_BETWEEN}
            loop={false}
          >
            {itemsWithPosters.map((item) => (
              <Button
                key={item._key}
                variant="unstyled"
                aria-label={`Play video: ${item.handle}`}
                position="relative"
                width="100%"
                height="100%"
                borderRadius="base"
                overflow="hidden"
                onClick={() => {
                  setActiveMedia(item);
                  onOpen();
                }}
              >
                {typeof item.poster === 'string' && (
                  <Image
                    src={item.poster}
                    alt=""
                    width={ASPECT_W * 100}
                    height={ASPECT_H * 100}
                    sizes={POSTER_SIZES}
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      aspectRatio: `${ASPECT_W} / ${ASPECT_H}`,
                    }}
                  />
                )}
                {typeof item.poster === 'object' && (
                  <WrappedImage
                    image={item.poster}
                    alt=""
                    sizes={POSTER_SIZES}
                    ratio={ratioToDecimal(ASPECT)}
                  />
                )}
                <PlayButton
                  aria-hidden
                  as="span"
                  size="s"
                  position="absolute"
                  bottom="2xs"
                  left="50%"
                  transform="translateX(-50%)"
                />
              </Button>
            ))}
          </CarouselContent>
        </Grid>
      </Box>
      <VideoLightbox
        isOpen={isOpen}
        onClose={onClose}
        cloudinaryId={activeMedia.video.cloudinaryId}
        videoTitle={activeMedia.video.title}
        poster={activeMedia.poster}
        aspectRatio={ASPECT}
        hasAudio={activeMedia.video.hasAudio}
      />
    </CarouselProvider>
  );
};
